// api
import type {
  EnergyInitiativeState,
  GetEnergyInitiativeDto,
  GetEnergyTreeNodeDto,
  PostEnergyInitiativeDto,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// relative
import type {
  InitiativeFormValues,
  SelectableEnergyTreeNode,
} from './initiativeForm.schema';

import { DateTime } from 'luxon';

const ZONE = 'Europe/Copenhagen';

function fromIsoToLuxon(iso?: string | null): DateTime | null {
  if (!iso) return null;
  const parsed = DateTime.fromISO(iso).setZone(ZONE);
  return parsed.isValid ? parsed : null;
}

function toUtcIso(value: DateTime | null): string | null {
  return value && value.isValid ? value.setZone('utc').toISO() : null;
}

export function toInitiativeFormValues(
  initiative?: GetEnergyInitiativeDto,
): InitiativeFormValues {
  return {
    name: initiative?.name ?? '',
    description: initiative?.description ?? '',
    energyTreeNodes: Array.isArray(initiative?.energyTreeNodes)
      ? initiative.energyTreeNodes
      : [],
    baselineStart: fromIsoToLuxon(initiative?.baselineStart),
    baselineEnd: fromIsoToLuxon(initiative?.baselineEnd),
    comparisonStart: fromIsoToLuxon(initiative?.comparisonStart),
    comparisonEnd: fromIsoToLuxon(initiative?.comparisonEnd),
  };
}

/**
 * Derives the initiative's lifecycle state purely from which period
 * boundaries have been set - mirrors the original stepper's logic.
 */
export function deriveInitiativeState(
  values: InitiativeFormValues,
): EnergyInitiativeState {
  if (
    values.baselineStart &&
    values.baselineEnd &&
    values.comparisonStart &&
    values.comparisonEnd
  )
    return 'Done';
  if (values.baselineStart && values.baselineEnd && values.comparisonStart)
    return 'Active';
  if (values.baselineStart && values.baselineEnd) return 'Initialized';
  if (values.baselineStart) return 'Initialized';
  return 'Created';
}

/**
 * `PutEnergyInitiativeDto` has an identical field shape to
 * `PostEnergyInitiativeDto`, so this single mapper's output is structurally
 * assignable to both mutations' payload types - reused by both Create and
 * Edit forms.
 */
export function toPostEnergyInitiativeDto(
  values: InitiativeFormValues,
): PostEnergyInitiativeDto {
  return {
    name: values.name,
    description: values.description || '',
    state: deriveInitiativeState(values),
    baselineStart: toUtcIso(values.baselineStart),
    baselineEnd: toUtcIso(values.baselineEnd),
    comparisonStart: toUtcIso(values.comparisonStart),
    comparisonEnd: toUtcIso(values.comparisonEnd),
    preferredModel: 0,
    energyTreeNodes: values.energyTreeNodes,
  };
}

export function asEnergyTreeNodeOption(
  node: GetEnergyTreeNodeDto,
): SelectableEnergyTreeNode {
  return {
    id: node?.energyTreeNodeId ?? '',
    type: node?.type,
    label: node?.name ?? String(node?.energyTreeNodeId ?? ''),
    hasData: (node?.dataStreams?.length ?? 0) > 0,
  };
}
